/**
 * Consent / age-gate route guard (P0, PRIV-006 / ADR-0009).
 *
 * The customer LIFF app must never render a screen other than the welcome
 * entry, the age-gate/consent screen itself, or error/loading states, until
 * the age-gate + privacy consent has been accepted (see `session-context.tsx`
 * `readStoredConsent` / `CONSENT_VERSION`). Extracted as pure functions (no
 * router import) so the redirect decision is unit-testable without spinning
 * up a full TanStack Router instance.
 */
import { readStoredConsent } from "./session-context.tsx";

/** Routes reachable without an accepted consent record. */
const CONSENT_EXEMPT_PATHS = ["/", "/welcome", "/age-gate"];

/** True when `pathname` is (or is nested under) one of the exempt paths. */
export function isConsentExemptPath(pathname: string): boolean {
  return CONSENT_EXEMPT_PATHS.some(
    (exempt) => pathname === exempt || pathname.startsWith(`${exempt}/`),
  );
}

/**
 * Resolve whether navigating to `pathname` must be redirected to `/age-gate`.
 * Returns `null` when the navigation may proceed as-is.
 */
export function resolveConsentGuardRedirect(pathname: string): "/age-gate" | null {
  if (isConsentExemptPath(pathname)) return null;
  return readStoredConsent().accepted ? null : "/age-gate";
}
